// src/components/DynamicInputs/NumberInputWithToggle.jsx
import React, { useState, useEffect } from 'react';
import TextInput from '../FormElements/TextInput.jsx';
import CheckboxInput from '../FormElements/CheckboxInput.jsx';

const NumberInputWithToggle = ({ schema, value: currentCompositeValue, onChange: driverOnChange }) => {
    const {
        initialValue: schemaInitialValue = { amount: '', noFixedBudget: false },
        componentProps = {},
        toggleCheckboxConfig = {},
        validation: schemaValidation,
        errorMessages: schemaErrorMessages,
        layout = {},
        id: questionId,
    } = schema;

    const initialValueKeys = Object.keys(schemaInitialValue);
    const amountKey = initialValueKeys.find(key => typeof schemaInitialValue[key] === 'string' || typeof schemaInitialValue[key] === 'number') || 'amount';
    const validation = schemaValidation || {};
    const errorMessages = schemaErrorMessages || {};

    const toggleKey = initialValueKeys.find(key => typeof schemaInitialValue[key] === 'boolean') || 'noFixedBudget';

    const safeInitialValue = { [amountKey]: '', [toggleKey]: false, ...schemaInitialValue };
    const currentVal = currentCompositeValue || safeInitialValue;

    const currentAmountStr = String(currentVal[amountKey] === null || currentVal[amountKey] === undefined ? '' : currentVal[amountKey]);
    const isToggled = !!currentVal[toggleKey];

    const [internalError, setInternalError] = useState('');

    useEffect(() => {
        if (isToggled) {
            setInternalError('');
        }
    }, [isToggled]);

    const validateAndReport = (newAmountStrParam, newIsToggled) => {
        let error = '';
        const amountToValidate = newIsToggled ? '' : newAmountStrParam;

        const finalCompositeValue = {
            [amountKey]: newIsToggled ? '' : newAmountStrParam,
            [toggleKey]: newIsToggled,
        };

        if (!newIsToggled) {
            if (validation.mainInputRequiredIfNotToggled && amountToValidate.trim() === '') {
                error = errorMessages.mainInputRequiredIfNotToggled || "Amount is required or toggle the checkbox.";
            } else if (amountToValidate.trim() !== '') {
                const numValue = parseFloat(amountToValidate);
                if (validation.numeric && isNaN(numValue)) {
                    error = errorMessages.numeric || "Please enter a valid number.";
                } else if (!isNaN(numValue)) {
                    if (validation.min !== undefined && numValue < validation.min) {
                        error = errorMessages.min || `Value must be at least ${validation.min}.`;
                    }
                    if (!error && validation.max !== undefined && numValue > validation.max) {
                        error = errorMessages.max || `Value must not exceed ${validation.max}.`;
                    }
                    if (!error && validation.integer && !/^-?\d+$/.test(amountToValidate)) {
                        error = errorMessages.integer || "Please enter a whole number.";
                    }
                }
            }
        }
        setInternalError(error);
        driverOnChange(finalCompositeValue, error);
    };

    const handleAmountChange = (event) => {
        const newAmountStr = event.target.value;
        validateAndReport(newAmountStr, isToggled);
    };

    const handleToggleChange = (event) => {
        const newIsToggled = event.target.checked;
        validateAndReport(currentAmountStr, newIsToggled);
    };

    // Enforce required props for number input
    const enforcedNumberProps = {
        max: 20,
        min: 1,
        placeholder: "1",
        inputClassName: "form-control form-control-lg text-center",
    };
    const mergedComponentProps = {
        ...componentProps,
        ...enforcedNumberProps,
    };
    const { placeholder, prefix, suffix, inputClassName: textInputSpecificClass } = mergedComponentProps;
    const { label: checkboxLabel, checkboxPosition = "below", wrapperClassName: checkboxWrapperClass, labelClassName: checkboxLabelClass } = toggleCheckboxConfig;

    const amountInputComponent = (
        <TextInput
            id={`${questionId}-${amountKey}`}
            name={`${questionId}-${amountKey}`}
            type="number"
            value={isToggled ? '' : currentAmountStr}
            onChange={handleAmountChange}
            placeholder={placeholder}
            disabled={isToggled}
            inputClassName={mergedComponentProps.inputClassName}
            ariaLabel={schema.title || "Amount"}
            min={mergedComponentProps.min}
            max={mergedComponentProps.max}
            noWrapper={true}
        />
    );

    const inputGroupComponent = (
        <div className={`input-group input-group-lg mb-3`}>
            {prefix && <span className="input-group-text">{prefix}</span>}
            {amountInputComponent}
            {suffix && <span className="input-group-text">{suffix}</span>}
        </div>
    );

    const checkboxComponent = (
        <CheckboxInput
            id={`${questionId}-${toggleKey}`}
            name={`${questionId}-${toggleKey}`}
            checked={isToggled}
            onChange={handleToggleChange}
            label={checkboxLabel}
            wrapperClassName={checkboxWrapperClass || "form-check mb-4"}
            labelClassName={checkboxLabelClass || "ms-3"}
        />
    );

    return (
        <div className={layout.answerOptionsContainerClassName || "mt-3"}>
            {checkboxPosition === 'above' && checkboxComponent}
            {inputGroupComponent}
            {!isToggled && internalError && (
                <div className="text-danger mb-3 ms-1" style={{ fontSize: '0.875em' }}>
                    {internalError}
                </div>
            )}
            {checkboxPosition === 'below' && checkboxComponent}
        </div>
    );
};
export default NumberInputWithToggle;